import { createCenterlineDXF } from "@/utils/lineworks-viewer";
import { distanceUnits } from "@/models/siteUnit.types";
import { createDistanceUnitPort } from "@/models/units/distanceUnitPort";

const resolveDistanceUnit = (unit) =>
	Object.values(distanceUnits).find((u) => u === unit) ?? distanceUnits.METERS;

const convertPointArrayToDisplay = (point, convertDistance) => {
	if (!Array.isArray(point) || point.length < 3) {
		return point;
	}

	const converted = [...point];
	converted[0] = convertDistance(point[0]);
	converted[1] = convertDistance(point[1]);
	converted[2] = convertDistance(point[2]);
	return converted;
};

const convertPositionToDisplay = (position, convertDistance) => {
	if (Array.isArray(position)) {
		return convertPointArrayToDisplay(position, convertDistance);
	}

	if (position && typeof position === "object") {
		return {
			...position,
			x: convertDistance(position.x),
			y: convertDistance(position.y),
			z: convertDistance(position.z),
		};
	}

	return position;
};

const convertCenterlineDataToDisplayUnit = (centerlineData, unit) => {
	if (!centerlineData || typeof centerlineData !== "object") {
		return centerlineData;
	}

	const safeUnit = resolveDistanceUnit(unit);
	const port = createDistanceUnitPort(safeUnit);
	const convertDistance = (value) =>
		typeof value === "number" ? port.toDisplay(value) : value;

	const converted = { ...centerlineData };

	if (Array.isArray(centerlineData.controlPoints)) {
		converted.controlPoints = centerlineData.controlPoints.map(
			(controlPoint) => {
				const nextControlPoint = { ...controlPoint };

				if (
					typeof controlPoint.x === "number" &&
					typeof controlPoint.y === "number" &&
					typeof controlPoint.z === "number"
				) {
					nextControlPoint.x = convertDistance(controlPoint.x);
					nextControlPoint.y = convertDistance(controlPoint.y);
					nextControlPoint.z = convertDistance(controlPoint.z);
				}

				if (Array.isArray(controlPoint.point)) {
					nextControlPoint.point = convertPointArrayToDisplay(
						controlPoint.point,
						convertDistance,
					);
				}

				return nextControlPoint;
			},
		);
	}

	if (centerlineData.pathInfo && typeof centerlineData.pathInfo === "object") {
		const nextPathInfo = { ...centerlineData.pathInfo };

		if (Array.isArray(centerlineData.pathInfo.pathPoints)) {
			nextPathInfo.pathPoints = centerlineData.pathInfo.pathPoints.map(
				(pathPoint) => ({
					...pathPoint,
					position: convertPositionToDisplay(
						pathPoint.position,
						convertDistance,
					),
				}),
			);
		}

		// Segment length is matched against distances derived from pathPoints
		// (BC/EC label placement), so both must use the same unit.
		if (Array.isArray(centerlineData.pathInfo.segments)) {
			nextPathInfo.segments = centerlineData.pathInfo.segments.map(
				(segment) => ({
					...segment,
					length: convertDistance(segment.length),
				}),
			);
		}

		converted.pathInfo = nextPathInfo;
	}

	return converted;
};

const downloadDxfBlob = (data, lineName) => {
	const blob = new Blob([data], { type: "application/dxf" });
	const link = document.createElement("a");
	link.href = URL.createObjectURL(blob);
	link.download = `${lineName || "CenterLine"}_line.dxf`;
	link.click();
	URL.revokeObjectURL(link.href);
};

const executeCenterlineDxfExport = async (
	rootState,
	{ viewerLineId, lineName, fetchCenterlineData, errorLabel = "Centerline" },
) => {
	try {
		if (!viewerLineId) {
			throw new Error("Invalid line data: missing viewerLineId");
		}

		let centerlineData = fetchCenterlineData();
		const siteDistanceUnit = rootState?.site?.units?.distance;

		if (!centerlineData) {
			throw new Error(
				`Failed to get centerline data for line "${lineName}" (ID: ${viewerLineId})`,
			);
		}
		if (typeof centerlineData === "string") {
			try {
				centerlineData = JSON.parse(centerlineData);
			} catch (parseError) {
				throw new Error(
					`Failed to parse centerline JSON data for line "${lineName}": ${parseError.message}`,
				);
			}
		}

		if (
			!centerlineData.controlPoints?.length &&
			!centerlineData.pathInfo?.pathPoints?.length
		) {
			throw new Error(
				`Invalid centerline data for line "${lineName}": no control points found`,
			);
		}

		centerlineData = convertCenterlineDataToDisplayUnit(
			centerlineData,
			siteDistanceUnit,
		);

		const response = await createCenterlineDXF(
			lineName,
			centerlineData,
			viewerLineId,
			siteDistanceUnit,
		);

		downloadDxfBlob(response.data, lineName);
		return { success: true };
	} catch (error) {
		console.error(`${errorLabel} DXF export failed:`, error);
		throw error;
	}
};

const executeLineSimaExport = async ({
	viewerLineId,
	lineName,
	siteName,
	exportMethods,
	errorLabel = "SIMA",
}) => {
	try {
		if (!viewerLineId) {
			throw new Error("Invalid line data: missing viewerLineId");
		}

		const filename = `${lineName}_line`;
		for (const method of exportMethods) {
			if (typeof window["viewer"]?.[method] === "function") {
				window["viewer"][method](viewerLineId, siteName, filename);
				return { success: true };
			}
		}

		throw new Error("No suitable export method found on viewer");
	} catch (error) {
		console.error(`${errorLabel} SIMA export failed:`, error);
		throw error;
	}
};

const actions = {
	// --- New Refactored Actions ---

	exportArbitraryLineToDXF({ rootState }, { viewerLineId, lineName }) {
		return executeCenterlineDxfExport(rootState, {
			viewerLineId,
			lineName,
			errorLabel: "Arbitrary line",
			fetchCenterlineData: () =>
				window["viewer"].fetchLineWorkJSON
					? window["viewer"].fetchLineWorkJSON(viewerLineId)
					: window["viewer"].getArbitraryLineJSON?.(viewerLineId),
		});
	},

	exportAlignmentToDXF({ rootState }, { viewerLineId, lineName }) {
		return executeCenterlineDxfExport(rootState, {
			viewerLineId,
			lineName,
			errorLabel: "Alignment",
			fetchCenterlineData: () =>
				window["viewer"].getCentralLineDataOfAlignment(viewerLineId),
		});
	},

	exportSIMAToDXF({ rootState }, { viewerLineId, lineName }) {
		return executeCenterlineDxfExport(rootState, {
			viewerLineId,
			lineName,
			errorLabel: "SIMA",
			fetchCenterlineData: () => {
				let data =
					window["viewer"].getCentralLineDataOfSIMA?.(viewerLineId) ??
					window["viewer"].getSIMALineJSON?.(viewerLineId);
				if (!data && window["viewer"].fetchLineWorkJSON) {
					data = window["viewer"].fetchLineWorkJSON(viewerLineId);
				}
				return data;
			},
		});
	},

	exportDXFToDXF({ rootState }, { viewerLineId, lineName }) {
		return executeCenterlineDxfExport(rootState, {
			viewerLineId,
			lineName,
			errorLabel: "DXF",
			fetchCenterlineData: () => {
				let data =
					window["viewer"].getCentralLineDataOfDXF?.(viewerLineId) ??
					window["viewer"].getDXFJSON?.(viewerLineId);
				if (!data && window["viewer"].fetchLineWorkJSON) {
					data = window["viewer"].fetchLineWorkJSON(viewerLineId);
				}
				return data;
			},
		});
	},

	exportArbitraryLineToSIMA(_, { viewerLineId, lineName, siteName }) {
		return executeLineSimaExport({
			viewerLineId,
			lineName,
			siteName,
			errorLabel: "Arbitrary line",
			exportMethods: ["exportArbitraryLineToSima", "exportLineWorksToSima"],
		});
	},

	exportAlignmentToSIMA(_, { viewerLineId, lineName, siteName }) {
		return executeLineSimaExport({
			viewerLineId,
			lineName,
			siteName,
			errorLabel: "Alignment",
			exportMethods: ["exportAlignmentToSima", "exportCenterlineToSima"],
		});
	},

	exportSIMAToSIMA(_, { viewerLineId, lineName, siteName }) {
		return executeLineSimaExport({
			viewerLineId,
			lineName,
			siteName,
			errorLabel: "SIMA",
			exportMethods: [
				"exportSIMAToSima",
				"exportSIMAToFile",
				"exportLineWorksToSima",
			],
		});
	},

	exportDXFToSIMA(_, { viewerLineId, lineName, siteName }) {
		return executeLineSimaExport({
			viewerLineId,
			lineName,
			siteName,
			errorLabel: "DXF",
			exportMethods: ["exportDXFToSima", "exportLineWorksToSima"],
		});
	},

	// --- Legacy Backward Compatibility Aliases ---

	async exportCenterline(context, payload) {
		return actions.exportAlignmentToDXF(context, payload);
	},

	async exportSima(context, payload) {
		return actions.exportAlignmentToSIMA(context, payload);
	},

	async exportLineWorksDXF(context, payload) {
		return actions.exportArbitraryLineToDXF(context, payload);
	},

	async exportLineWorksSima(context, payload) {
		return actions.exportArbitraryLineToSIMA(context, payload);
	},
};

export default {
	namespaced: true,
	actions,
};
